<script setup>
import { isNil, get } from 'lodash-es'
import { formatScientific } from '@/utils/formatters'
import { getClassForBooleanText, getColorStyle } from '@/components/combined-predict/models/utils/displayUtils'
import { getPredictionRawValue } from '@/components/combined-predict/models/opera/operaColumnsDef'
import { mapUnitOrOriginal } from '@/components/combined-predict/components/CombinedPredictTable/utils/unitConversions'
import { formatRange } from '@/components/combined-predict/utils'
import ChemPropsWithCopyCard from '@/components/chemical/ChemPropsWithCopyCard.vue'
import {
  getCategoryTextByCategory,
  categoricalEndpoints,
} from '@/components/combined-predict/models/opera/operaMetadata'

const popoverFields = [
  { key: 'predicted.value', label: 'Value' },
  { key: 'experimental.value', label: 'Experimental Value' },
  { key: 'predicted.range', label: 'Range' },
  { key: 'predicted.ad', label: 'Applicability Domain' },
  { key: 'predicted.ad_index', label: 'AD Index' },
  { key: 'predicted.conf_index', label: 'Confidence Index' },
]

const getEndpointResult = (tooltipParams) => tooltipParams?.endpointResult || null

const getEndpointResultContext = (tooltipParams, fieldKey) => {
  const endpointResult = getEndpointResult(tooltipParams)
  return {
    endpointResult: endpointResult,
    value: get(endpointResult, fieldKey, null),
  }
}

const parseFieldKey = (fieldKey) => {
  const [sourceType, valueKey] = fieldKey.split('.')
  return { sourceType, valueKey }
}

const formatFieldValue = (tooltipParams, fieldKey) => {
  const { endpointResult, value } = getEndpointResultContext(tooltipParams, fieldKey)
  const { sourceType, valueKey } = parseFieldKey(fieldKey)

  if (!endpointResult) return ''

  if (valueKey === 'value') {
    const unit = tooltipParams?.unit
    const sourceResult = get(endpointResult, sourceType)
    const rawValue = getPredictionRawValue({ endpointResult, sourceResult })
    const formattedValue = formatScientific(rawValue, {
      unit,
      blank: null,
    })

    const endpointId = endpointResult?.endpoint_id

    if (categoricalEndpoints.includes(endpointId)) {
      const categoryText = getCategoryTextByCategory(value, endpointId)
      return categoryText
    }

    return formattedValue
  }

  if (valueKey === 'range') return value ? formatRange(value, tooltipParams?.unit) : ''
  if (valueKey === 'ad') return isNil(value) ? '' : value ? 'Yes' : 'No'
  return formatScientific(value, { blank: null })
}

const getColorValue = (tooltipParams, fieldKey) => {
  const { endpointResult, value } = getEndpointResultContext(tooltipParams, fieldKey)
  const { sourceType, valueKey } = parseFieldKey(fieldKey)

  if (isNil(value)) return null
  if (valueKey === 'ad_index' || valueKey === 'conf_index') return value

  return null
}
</script>

<template>
  <v-card
    v-if="params?.data?.chemical && params?.prediction"
    max-width="30rem"
    min-width="20rem"
    max-height="36rem"
    class="overflow-auto"
  >
    <ChemPropsWithCopyCard
      :chemical="params?.data?.chemical"
      :props-to-show="['casrn', 'inchiKey']"
      key-value-stacked
      show-in-row
      truncate-props
    />

    <v-divider class="mx-4 my-0" />

    <v-card-text>
      <b-table-lite
        :fields="popoverFields"
        :items="[params?.prediction]"
        borderless
        small
        stacked
        class="mb-0"
      >
        <!-- <template #cell(value)="data">
          <div v-if="data.item">
            {{ formatFieldValue(data.item, data.field.key) }}
            <span
              v-if="formatFieldValue(data.item, data.field.key) && data?.item.unit"
              class="text-muted small"
              >{{ mapUnitOrOriginal(data?.item.unit) }}</span
            >
          </div>

          <span
            v-else
            class="text-muted"
          >
            –
          </span>
        </template> -->

        <template #cell()="data">
          <div>
            <span
              v-if="getColorValue(data.item, data.field.key) != null"
              :style="getColorStyle(getColorValue(data.item, data.field.key))"
            >
              {{ formatFieldValue(data.item, data.field.key) || '–' }}
            </span>

            <div
              v-else
              :class="getClassForBooleanText(formatFieldValue(data.item, data.field.key))"
            >
              <span>
                {{ formatFieldValue(data.item, data.field.key) || '–' }}
              </span>

              <span
                v-if="
                  formatFieldValue(data.item, data.field.key) &&
                  data?.item.unit &&
                  parseFieldKey(data.field.key).valueKey === 'value'
                "
                class="text-muted small"
              >
                {{ mapUnitOrOriginal(data?.item.unit) }}
              </span>
            </div>
          </div>
        </template>
      </b-table-lite>
    </v-card-text>
  </v-card>

  <v-card
    v-else
    class="p-2"
  >
    {{ params?.column?.colDef?.headerTooltip }}
  </v-card>
</template>
